// src/components/ChargerStatusBadge.tsx
import React, { useState } from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  Dimensions,
  Platform,
} from 'react-native';
import {
  ChargerAttributes,
  ChargerDisplayStatus,
  ChargerErrorInfo,
  mapConnectorStatusToDisplay,
  getStatusColor,
  hasChargerErrorInfo,
  isStatusTappable,
  getChargerErrorInfo,
  isChargerOffline,
} from '../models/charger-model';
import ChargerStatusDetailModal from './ChargerStatusDetailModal';
import {
  isCurrentlyUnavailable,
  getUnavailabilitySummary,
} from '../components/common/utils/unavailable-times-utils';

const { width } = Dimensions.get('window');

interface ChargerStatusBadgeProps {
  charger: ChargerAttributes;
  size?: 'small' | 'medium' | 'large';
  style?: object;
}

/**
 * Determines the display status for a charger based on multiple factors:
 * 1. Registration status (pending)
 * 2. OCPP connector status (for OCPP chargers - from flat field or nested chargerState)
 * 3. Legacy isOccupied/is_enabled flags
 */
const getChargerDisplayStatus = (charger: ChargerAttributes): ChargerDisplayStatus => {
  // Check registration status first
  if (charger.registration_status === 'pending_charger') {
    return 'Pending';
  }

  // Check if charger is offline (connected === false)
  if (isChargerOffline(charger)) {
    return 'Charger Offline';
  }

  // Check OCPP connector status if available (flat field first, then nested)
  const connectorStatus = charger.ocpp_connector_status || charger.chargerState?.connector_status;
  if (connectorStatus) {
    return mapConnectorStatusToDisplay(connectorStatus);
  }

  // Fall back to legacy status determination
  if (charger.isOccupied === 1 || charger.is_enabled === false) {
    return 'In Use';
  }

  // Check scheduled unavailable times (prefer backend-computed flag, fall back to local check)
  if (charger.is_currently_unavailable === true) {
    return 'Unavailable';
  }
  if (
    charger.is_currently_unavailable === undefined &&
    isCurrentlyUnavailable(charger.unavailable_times ?? null)
  ) {
    return 'Unavailable';
  }

  return 'Available';
};

/**
 * Gets the badge width based on status text and size
 */
const getBadgeWidth = (status: ChargerDisplayStatus, size: 'small' | 'medium' | 'large'): number => {
  const baseWidths: Record<ChargerDisplayStatus, number> = {
    'Available': 100,
    'In Use': 80,
    'Faulted': 90,
    'Charger Error': 130,
    'Unavailable': 110,
    'Charger Offline': 140,
    'Pending': 100,
    'Preparing': 100,
    'Vehicle Connected': 150,
    'Finishing': 100,
    'Reserved': 100,
    'SuspendedEV': 120,
    'SuspendedEVSE': 130,
  };

  const sizeMultiplier = size === 'small' ? 0.85 : size === 'large' ? 1.15 : 1;
  return Math.min(baseWidths[status] * sizeMultiplier, width * 0.3);
};

const ChargerStatusBadge: React.FC<ChargerStatusBadgeProps> = ({
  charger,
  size = 'medium',
  style,
}) => {
  const [modalVisible, setModalVisible] = useState(false);

  const displayStatus = getChargerDisplayStatus(charger);
  const errorInfo = getChargerErrorInfo(charger);
  const severity = errorInfo?.severity || charger.error_severity;
  const backgroundColor = getStatusColor(displayStatus, severity);
  const isTappable = isStatusTappable(displayStatus, charger);
  const unavailHint = getUnavailabilitySummary(charger.unavailable_times ?? null);

  const handlePress = () => {
    if (isTappable) {
      setModalVisible(true);
    }
  };

  const fontSize = size === 'small' ? 10 : size === 'large' ? 14 : 12;
  const paddingVertical = size === 'small' ? 4 : size === 'large' ? 10 : 8;
  const paddingHorizontal = size === 'small' ? 10 : size === 'large' ? 20 : 16;
  const badgeWidth = getBadgeWidth(displayStatus, size);

  // Get display text - show short user message if faulted, otherwise show status
  let displayText = displayStatus;
  const userMessage = errorInfo?.user_message || charger.error_user_message;
  if (displayStatus === 'Faulted' && userMessage) {
    // Get first part before dash or first few words
    const shortMessage = userMessage.split(' - ')[0];
    if (shortMessage.length <= 20) {
      displayText = shortMessage as any;
    }
  }

  const badgeContent = (
    <View
      style={[
        styles.badge,
        {
          backgroundColor,
          paddingVertical,
          paddingHorizontal,
          minWidth: badgeWidth,
        },
        isTappable && styles.tappableBadge,
        style,
      ]}
    >
      <Text
        style={[
          styles.badgeText,
          { fontSize },
        ]}
        numberOfLines={1}
        ellipsizeMode="tail"
      >
        {displayText}
      </Text>
      {isTappable && (
        <Text style={[styles.infoIcon, { fontSize: fontSize - 2 }]}>ⓘ</Text>
      )}
    </View>
  );

  return (
    <>
      <View style={styles.badgeStack}>
        {isTappable ? (
          <Pressable onPress={handlePress} accessibilityRole="button" accessibilityHint="Tap for more details">
            {badgeContent}
          </Pressable>
        ) : (
          badgeContent
        )}

        {unavailHint && displayStatus !== 'Unavailable' && (
          <View style={styles.unavailHintBadge}>
            <Text style={styles.unavailHintText}>{unavailHint}</Text>
          </View>
        )}
      </View>

      {hasChargerErrorInfo(charger) && (
        <ChargerStatusDetailModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          chargerName={charger.name}
          status={displayStatus}
          errorCode={errorInfo?.error_code || charger.error_code}
          errorDescription={errorInfo?.description || charger.error_description}
          errorUserMessage={errorInfo?.user_message || charger.error_user_message}
          errorSeverity={errorInfo?.severity || charger.error_severity}
          errorRecommendedAction={errorInfo?.recommended_action || charger.error_recommended_action}
          vendorId={charger.chargerState?.vendor_id || charger.vendor_id}
          vendorErrorCode={charger.chargerState?.vendor_error_code || charger.vendor_error_code}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  badgeStack: {
    alignItems: 'center',
    gap: 4,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 23,
    gap: 4,
  },
  unavailHintBadge: {
    backgroundColor: '#92400E',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  unavailHintText: {
    color: '#FCD34D',
    fontSize: 9,
    fontWeight: '600',
  },
  tappableBadge: {
    // Add subtle indication that it's tappable
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.2,
        shadowRadius: 2,
      },
      android: {
        elevation: 2,
      },
      web: {
        cursor: 'pointer',
      },
    }),
  },
  badgeText: {
    color: '#FFFFFF',
    fontWeight: '700',
    textAlign: 'center',
  },
  infoIcon: {
    color: '#FFFFFF',
    opacity: 0.9,
  },
});

export default ChargerStatusBadge;
